import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';
import { NotificationService } from 'ui-components/services/notification-service';

import { useSystemAutomation } from '../../../../contexts/system-automation-context';
import { useCustomRateShopperSetupCompleteSellerSetting } from '../../../../hooks/use-seller-setting';
import { useFilteredPackages } from '../../../../hooks/use-filtered-packages';
import { LogService } from '../../../../services/log-service';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import Global from '../../../../store/global-selectors';
import { refreshSellerDataByProperty, saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { updateSellerSettingsBulk } from '../../api/save-settings';
import {
  getSellerServiceCollections,
  upsertServiceCollection,
  deleteServiceCollection,
  toggleRateShopper,
} from '../../api/service-collections';
import { NO_OPTION_TYPE } from '../components/rate-shopper-page/new-shopper-options/constants';
import { NOTIFICATIONS } from '../constants';
import { useRateShopper } from '../hooks/use-rate-shopper';
import { accountNicknameIsExcluded } from '../services/excluded-account-nicknames';

export const RateShopperContext = React.createContext();

export const useRateShopperContext = () => useContext(RateShopperContext);

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const RateShopperContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isCustomRateShopperSetupComplete = useCustomRateShopperSetupCompleteSellerSetting();
  const { requirePackage } = useRateShopper();
  // there is a requireConfirmation prop too but we are using just the package since we won't have a need to toggle off for just confirmation
  const [rateShoppers, setRateShoppers] = useState([]);
  const [usePackageAndConfirmation, setUsePackageAndConfirmation] = useState(requirePackage);
  const { automationRulesHash, refreshAutomationRules } = useSystemAutomation();
  const [isRequestingRateShoppers, setIsRequestingRateShoppers] = useState(false);
  const merchantServices = useSelector(Global.selectSellerServices);
  const accounts = useSelector(Global.selectSellerLabelProviders);
  const packages = useFilteredPackages();
  const carriers = useSelector(Global.selectReferenceDatagetSellerCarriers);

  const onUsePackageAndConfirmationChange = useCallback(() => {
    setUsePackageAndConfirmation(!usePackageAndConfirmation);
  }, [usePackageAndConfirmation, setUsePackageAndConfirmation]);
  const [templateOptionType, setTemplateOptionType] = useState(NO_OPTION_TYPE);

  const refreshServiceCollections = useCallback(() => {
    setIsRequestingRateShoppers(true);
    getSellerServiceCollections()
      .then((res) => {
        setIsRequestingRateShoppers(false);
        setRateShoppers(res);
        dispatch(refreshSellerDataByProperty('serviceCollections', res));

        refreshAutomationRules(res);
      })
      .catch((e) => {
        setIsRequestingRateShoppers(false);
        LogService.logError(new Error('Failed to fetch shipping active Rate Shoppers', { cause: e }));
      });
  }, [dispatch, refreshAutomationRules]);

  useEffect(() => {
    refreshServiceCollections();
  }, [refreshServiceCollections]);

  const saveRateShopper = useCallback(
    ({ serviceCollectionGuid, name, services, isActive, maxTransitDays, cheapestOverride, useDeliverByDate }) => {
      upsertServiceCollection({
        serviceCollectionGuid,
        name,
        services: services.map((service) => ({
          ...service,
          shippingServiceId: service.serviceId,
          sellerProviderId: service.sellerLabelProviderId,
        })),
        isActive,
        maxTransitDays,
        useDeliverByDate,
        cheapestOverride: cheapestOverride && {
          ...cheapestOverride,
          sellerProviderId: Number.parseInt(cheapestOverride.sellerProviderId, 10),
          shippingServiceId: Number.parseInt(cheapestOverride.shippingServiceId, 10),
        },
      })
        .then(() => {
          refreshServiceCollections();
          if (!isCustomRateShopperSetupComplete) {
            updateSellerSettingsBulk({ [SellerSettings.isCustomRateShopperSetupComplete.key]: 'true' })
              .then((newSettings) => dispatch(saveNewSellerSettings(newSettings)))
              .catch((e) => LogService.logError(new Error('Failed to save custom rate shopper setup setting', { cause: e })));
          }
        })
        .catch((e) => {
          LogService.logError(new Error('Failed to save rate shopper', { cause: e }));
        });
    },
    [dispatch, isCustomRateShopperSetupComplete, refreshServiceCollections],
  );

  const deleteRateShopper = useCallback(
    async (shopper) => {
      try {
        await deleteServiceCollection(shopper.serviceCollectionGuid);
        refreshServiceCollections();
        return true;
      } catch {
        NotificationService.showNotification(NOTIFICATIONS.deleteFailure);
        return false;
      }
    },
    [refreshServiceCollections],
  );

  const toggleActiveRateShopper = useCallback(
    async (serviceCollectionGuid, activeStatus) => {
      try {
        await toggleRateShopper(serviceCollectionGuid, activeStatus);
        refreshServiceCollections();
        return true;
      } catch {
        NotificationService.showNotification(NOTIFICATIONS.errorToggle);
        return false;
      }
    },
    [refreshServiceCollections],
  );

  // * = named differently on the api/serviceCollection response
  const shoppersWithMappedServices = useMemo(
    () =>
      rateShoppers.map((shopper) => {
        const services = shopper.services.map((service) => {
          const merchantService = merchantServices.find(
            (_merchantService) =>
              _merchantService.serviceId === service.shippingServiceId && _merchantService.labelProviderId === service.providerId,
          );
          const accountDetails = accounts.find((account) => account.sellerLabelProviderId === service.sellerProviderId);
          const accountNickname = accountDetails?.name && !accountNicknameIsExcluded(accountDetails.name) ? accountDetails.name : null;
          const packageName = packages.find(
            (p) => p.packageTypeId === service.packageTypeId && p.sellerLabelProviderId === merchantService.sellerProviderId,
          )?.name;
          const carrierInfo = carriers.find((carrier) => carrier.carrierId === service.carrierId);
          const carrierConfirmationTypes = carrierInfo?.confirmations || emptyArray;
          const confirmationName = carrierConfirmationTypes.find((conf) => conf.confirmationId === service.confirmationId)?.name;

          return {
            confirmationId: service.confirmationId,
            labelProviderId: service.providerId, // *
            packageTypeId: service.packageTypeId,
            sellerLabelProviderId: service.sellerProviderId, // *
            serviceCollectionGuid: service.serviceCollectionGuid,
            serviceId: service.shippingServiceId, // *
            carrierId: merchantService?.carrierId,
            name: merchantService?.name,
            isInternational: merchantService?.features?.supportsInternationalShipping,
            accountNickname,
            packageName,
            confirmationName,
          };
        });
        return {
          ...shopper,
          services,
        };
      }),
    [rateShoppers, merchantServices, accounts, packages, carriers],
  );

  const data = {
    deleteRateShopper,
    saveRateShopper,
    toggleActiveRateShopper,
    rateShoppers: shoppersWithMappedServices,
    automationRulesHash,
    refreshServiceCollections,
    usePackageAndConfirmation,
    onUsePackageAndConfirmationChange,
    isRequestingRateShoppers,
    templateOptionType,
    setTemplateOptionType,
  };

  return <RateShopperContext.Provider value={data}>{children}</RateShopperContext.Provider>;
};
